import { useCallback, useEffect, useRef, useState } from "react";
import { selectionHistoryApi } from "src/Api/selectionHistory";

// Shared across all components that import this module
const constructionCache = new Map<number, any[]>();
const inFlightRequests = new Map<number, Promise<any[]>>();

function fetchConstructionSelectionHistoriesOnce(userId: number) {
  if (!userId) return [];

  // If already fetching, return the same promise
  if (inFlightRequests.has(userId)) {
    return inFlightRequests.get(userId)!;
  }

  const request = selectionHistoryApi
    .getConstructionSelectionHistories(userId)
    .then(res => {
      const data = res.data.data;
      constructionCache.set(userId, data);
      inFlightRequests.delete(userId);
      return data;
    })
    .catch(err => {
      inFlightRequests.delete(userId);
      throw err;
    });

  inFlightRequests.set(userId, request);
  return request;
}

let globalInstanceCounter = 0;

export function useConstructionSelectionHistories(userId: number) {
  const [constructionList, setConstructionList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const instanceIdRef = useRef(++globalInstanceCounter);

  // DEBUG: log mount/unmount
  useEffect(() => {
    //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] MOUNT userId=${userId}`);
    return () => {
      //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] UNMOUNT userId=${userId}`);
    };
  }, []);

  // DEBUG: log userId changes
  useEffect(() => {
    //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] userId changed to: `, userId);
  }, [userId]);

  // Wrap the state setter so we can see exactly what’s going on
  const debugSetConstructionList = useCallback(
    (next: any[]) => {
      //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] setConstructionList len=${next.length}`, { userId });
      setConstructionList(next);
    },
    [userId]
  );

  const fetchConstructionList = useCallback(
    async (options?: { force?: boolean }) => {
      if (!userId) {
        //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] fetch skipped, falsy userId: `, userId);
        return;
      }

      const force = options?.force ?? false;

      // If we already have cached data for this user and not forcing, just use it.
      if (!force && constructionCache.has(userId)) {
        const cached = constructionCache.get(userId)!;
        //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] using CACHE len=${cached.length} and userId=${userId}`);
        debugSetConstructionList(cached);
        setLoading(false);
        return;
      }

      //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] starting FETCH userId=${userId}, force=${force}`);
      setLoading(true);
      try {
        const data = await fetchConstructionSelectionHistoriesOnce(userId);
        //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] FETCH DONE len=${data.length} and userId=${userId}`);
        debugSetConstructionList(data);
      } finally {
        setLoading(false);
      }
    },
    [userId, debugSetConstructionList]
  );

  // initial / on-userId-change load
  useEffect(() => {
    if (!userId) return;

    // If there's cached data already, use it immediately
    if (constructionCache.has(userId)) {
      const cached = constructionCache.get(userId)!;
      //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] effect using CACHE len=${cached.length} and userId=${userId}`);
      debugSetConstructionList(cached);
      setLoading(false);
      return;
    }

    // Otherwise, fetch (this may reuse an in-flight request)
    //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] effect triggering fetch, userId=${userId}`);
    fetchConstructionList();
  }, [userId, fetchConstructionList]);

  // log loading changes
  useEffect(() => {
    //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] loading=${loading}`);
  }, [loading]);

  // POST（保存&リスト取得）
  const storeConstructionSelection = useCallback(
    async (body: any) => {
      if (!userId) return;
      //console.log(`[useConstructionSelectionHistories #${instanceIdRef.current}] storeConstructionSelection`, body);
      await selectionHistoryApi.storeConstructionSelectionHistory(body);

      // Invalidate cache for this user
      constructionCache.delete(userId);

      // Force refetch (will create a new request, shared across components)
      await fetchConstructionList({ force: true });
    },
    [userId, fetchConstructionList]
  );

  return { constructionList, loading, storeConstructionSelection, fetchConstructionList };
}